import React from 'react';
import { NoAssetsSelectedImage } from '../../../../../../../assets/images';
import { Image, Icon, Text, ErrorIcon, Button } from '../../../../../../../ui-kit';
import CreditsOverview from '../../../../CreditsOverview/index';
import { CURRENCY_SYMBOLS } from '../../../../../../../common/constants/currency';

import '../styles/_credit-menu-subscription-v22.scss';

const formatDollars = (amount, currency) => {
  if (typeof amount !== 'number') return '';
  const symbol = (currency && CURRENCY_SYMBOLS[currency]) || '$';
  const value = Number.isInteger(amount)
    ? amount.toLocaleString()
    : amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${symbol}${value}` : `${symbol}${value}`;
};

const errorMessage = {
  WORKSPACE: 'Your workspace has ran out of credits for this month. Continue submitting projects by topping up credits now.',
  TEAM: 'This team has ran out of credits. Continue submitting projects with this team by topping up credits now.'
};

const warningMessage = {
  WORKSPACE: 'Your workspace is about to run out of credits for this month. Avoid the last minute chaos by topping up now.',
  TEAM: 'The team is about to run  out of credits. Avoid the last minute chaos by topping up now.'
};

export default function CreditMenuSubscription({
  creditType,
  isUnlimited,
  creditBalance,
  teams,
  onToggleTopupModal,
  contractCreditsBalance,
  organisationInfo,
  financeLedgerEnabled = false,
  creditsRemaining,
  dollarsRemaining,
  currency,
  poolRemaining,
  financeTeamRows = []
}) {
  const headingText = financeLedgerEnabled ? 'Balance' : 'Available Credits';
  const showDollars =
    financeLedgerEnabled && typeof dollarsRemaining === 'number';
  const showFinanceCredits =
    financeLedgerEnabled && typeof creditsRemaining === 'number';
  const buttonText = financeLedgerEnabled ? 'Top Up Credits' : 'Top Up Now';

  // In finance mode, use financeTeamRows to determine whether teams are present.
  // In contract mode, use the legacy teams array.
  const hasVisibleTeams = financeLedgerEnabled
    ? financeTeamRows.length > 0
    : teams.length > 0;

  if (creditType === 'TEAM' && !hasVisibleTeams) {
    return (
      <div className="credit-menu-subscription-v22__wrapper">
        <Text heading5>{headingText}</Text>

        <div className="credit-menu-subscription-v22--no-teams">
          <Image
            width="140px"
            height="76px"
            placeholderText="no-team"
            placeholderTextSize={15}
            defaultIconColour="regent"
            imageUrl={NoAssetsSelectedImage} />

          <Text centered highlighted noMargin>No teams available</Text>
          <Text noMargin>
            Contact your Workspace Admin to be invited to a team in order
            to submit projects.
          </Text>
        </div>
        <Button
          className="credit-menu-subscription-v22--no-teams__button"
          onClick={void 0}
          type="PRIMARY"
          prefix={<Icon name="flashon" size={20} color="regent" />}
          disabled>

          {buttonText}
        </Button>
      </div>);

  }

  if (isUnlimited) {
    return (
      <div className="credit-menu-subscription-v22__share">
        <CreditsOverview
          creditBalance={creditBalance}
          isUnlimited={isUnlimited} />
      </div>);

  }

  const financeBalanceTotal = showFinanceCredits ? creditsRemaining : 0;
  const financeWarningMessage = creditType === 'TEAM' ? warningMessage.TEAM : warningMessage.WORKSPACE;
  const financeErrorMessage = creditType === 'TEAM' ? errorMessage.TEAM : errorMessage.WORKSPACE;
  const orgName = organisationInfo?.name || '';
  const orgImageUrl = organisationInfo?.imageUrl || '';

  return (
    <div className="credit-menu-subscription-v22">
      <div className="credit-menu-subscription-v22__wrapper" style={{ maxHeight: `${window.screen.height}px` }}>
        <Text heading5>{headingText}</Text>
        {financeLedgerEnabled ?
        <div className="credit-menu-subscription-v22__balance">
            {/* Org / workspace total card — always shown in finance mode */}
            <div className="credit-menu-subscription-v22__balance__card">
              <Text
                heading5
                noMargin
                className="credit-menu-subscription-v22__balance__card__name">
                {orgName}
              </Text>
              <div className="credit-menu-subscription-v22__balance__card__body">
                <div className="credit-menu-subscription-v22__balance__card__image">
                  <Image
                    width="50px"
                    height="50px"
                    placeholderText={orgName}
                    placeholderTextSize={15}
                    defaultIconColour="regent"
                    imageUrl={orgImageUrl}
                    rounded />

                </div>
                <div className="credit-menu-subscription-v22__balance__card__info">
                  <div className="credit-menu-subscription-v22__balance__card__info__stats">
                    {showFinanceCredits &&
                    <div className="credit-menu-subscription-v22__balance__card__info__stats__stat">
                      <Text
                        label
                        noMargin
                        className="credit-menu-subscription-v22__balance__card__info__stats__stat__label">
                        Credits
                      </Text>
                      <Text
                        label
                        noMargin
                        className="credit-menu-subscription-v22__balance__card__info__stats__stat__value">
                        {creditsRemaining}
                      </Text>
                    </div>}

                    {showFinanceCredits && showDollars &&
                    <span className="credit-menu-subscription-v22__balance__card__info__stats__divider" />}

                    {showDollars &&
                    <div className="credit-menu-subscription-v22__balance__card__info__stats__stat">
                      <Text
                        label
                        noMargin
                        className="credit-menu-subscription-v22__balance__card__info__stats__stat__label">
                        S-Dollars
                      </Text>
                      <Text
                        label
                        noMargin
                        className="credit-menu-subscription-v22__balance__card__info__stats__stat__value">
                        {formatDollars(dollarsRemaining, currency)}
                      </Text>
                    </div>}

                  </div>
                </div>
                {showFinanceCredits && financeBalanceTotal <= 1 &&
                <ErrorIcon
                  id="workspace-no-credits"
                  className="credit-menu-subscription-v22__balance__card__warning"
                  tooltipProps={{ type: `${financeBalanceTotal === 1 ? 'warning' : ''}` }}
                  errorMessage={financeBalanceTotal === 1 ?
                  financeWarningMessage :
                  financeErrorMessage} />}
              </div>
            </div>

            {/* TEAM mode: Workspace Pool card + per-team cards */}
            {creditType === 'TEAM' && (
              <>
                {/* Workspace Pool card */}
                {typeof poolRemaining === 'number' && (
                  <div className="credit-menu-subscription-v22__teams">
                    <div className="credit-menu-subscription-v22__teams__team">
                      <div className="credit-menu-subscription-v22__teams__team__image">
                        <Image
                          width="50px"
                          height="50px"
                          placeholderText="Pool"
                          placeholderTextSize={15}
                          defaultIconColour="regent"
                          rounded />

                      </div>
                      <div className="credit-menu-subscription-v22__teams__team__info">
                        <Text heading5 noMargin>Workspace Pool</Text>
                        <div className="credit-menu-subscription-v22__teams__team__info__credits">
                          <Text label noMargin>{`${poolRemaining} ${poolRemaining === 1 ? 'credit' : 'credits'}`}</Text>
                        </div>
                      </div>
                      {poolRemaining <= 1 &&
                      <ErrorIcon
                        id="pool-no-credits"
                        className="credit-menu-subscription-v22__teams__team__info__credits__warning"
                        tooltipProps={{ type: `${poolRemaining === 1 ? 'warning' : ''}` }}
                        errorMessage={poolRemaining === 1 ?
                        warningMessage.WORKSPACE :
                        errorMessage.WORKSPACE} />}


                    </div>
                  </div>
                )}

                {/* Per-team cards */}
                {financeTeamRows.map((teamRow) => {
                  const {
                    teamId,
                    name: teamName,
                    imageUrl: teamImageUrl,
                    creditsRemaining: teamCredits
                  } = teamRow;
                  return (
                    <div key={teamId} className="credit-menu-subscription-v22__teams">
                      <div className="credit-menu-subscription-v22__teams__team">
                        <div className="credit-menu-subscription-v22__teams__team__image">
                          <Image
                            width="50px"
                            height="50px"
                            placeholderText={teamName}
                            placeholderTextSize={15}
                            defaultIconColour="regent"
                            imageUrl={teamImageUrl || ''}
                            rounded />

                        </div>
                        <div className="credit-menu-subscription-v22__teams__team__info">
                          <Text heading5 noMargin>{teamName}</Text>
                          <div className="credit-menu-subscription-v22__teams__team__info__credits">
                            <Text label noMargin>{`${teamCredits} ${teamCredits === 1 ? 'credit' : 'credits'}`}</Text>
                          </div>
                        </div>
                        {teamCredits <= 1 &&
                        <ErrorIcon
                          id={`team-no-credits-${teamId}`}
                          className="credit-menu-subscription-v22__teams__team__info__credits__warning"
                          tooltipProps={{ type: `${teamCredits === 1 ? 'warning' : ''}` }}
                          errorMessage={teamCredits === 1 ?
                          warningMessage.TEAM :
                          errorMessage.TEAM} />}


                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div> :

        contractCreditsBalance.map((contractBalance) => {
          const {
            credit = 0,
            topUpCredit = 0,
            teamId = '',
            product = '',
            name = '',
            imageUrl = ''
          } = contractBalance || {};
          const totalAvailableCredits = credit + topUpCredit;

          const warningMessageToShow = product ? warningMessage.WORKSPACE : warningMessage.TEAM;
          const errorMessageToShow = product ? errorMessage.WORKSPACE : errorMessage.TEAM;

          return (
            <div key={teamId} className="credit-menu-subscription-v22__teams">
              <div className="credit-menu-subscription-v22__teams__team">
                <div className="credit-menu-subscription-v22__teams__team__image">
                  <Image
                    width="50px"
                    height="50px"
                    placeholderText={name}
                    placeholderTextSize={15}
                    defaultIconColour="regent"
                    imageUrl={imageUrl || ''}
                    rounded />

                </div>
                <div className="credit-menu-subscription-v22__teams__team__info">
                  <Text heading5 noMargin>{name}</Text>
                  <div className="credit-menu-subscription-v22__teams__team__info__credits">
                    <Text label noMargin>{`${totalAvailableCredits} credits`}</Text>
                  </div>
                </div>
                {totalAvailableCredits <= 1 &&
                <ErrorIcon
                  id="workspace-no-credits"
                  className="credit-menu-subscription-v22__teams__team__info__credits__warning"
                  tooltipProps={{ type: `${totalAvailableCredits === 1 ? 'warning' : ''}` }}
                  errorMessage={totalAvailableCredits === 1 ?
                  warningMessageToShow :
                  errorMessageToShow} />}



              </div>
            </div>);

        })}

        <div
          className={`credit-menu-subscription-v22__teams__button${
          financeLedgerEnabled ? ' credit-menu-subscription-v22__teams__button--finance' : ''
          }`}>

          <Button
            onClick={onToggleTopupModal}
            type="PRIMARY"
            prefix={<Icon name="flashon" size={20} color={financeLedgerEnabled ? 'white' : 'darkDenim'} />}>

            {buttonText}
          </Button>
        </div>
      </div>
    </div>);

}
